// src/context/ToolsContext.jsx
import React, { createContext, useContext, useEffect, useState } from 'react';

const ToolsContext = createContext();

// IndexedDB helpers
const DB_NAME = 'KoruAIDB';
const DB_VERSION = 1;
const STORE_NAME = 'tools';

const openDB = () => {
    return new Promise((resolve, reject) => {
        const request = indexedDB.open(DB_NAME, DB_VERSION);
        request.onerror = () => reject(request.error);
        request.onsuccess = () => resolve(request.result);
        request.onupgradeneeded = (event) => {
            const db = event.target.result;
            if (!db.objectStoreNames.contains(STORE_NAME)) {
                db.createObjectStore(STORE_NAME);
            }
        };
    });
};

const saveToIndexedDB = async (key, data) => {
    const db = await openDB();
    return new Promise((resolve, reject) => {
        const transaction = db.transaction([STORE_NAME], 'readwrite');
        const store = transaction.objectStore(STORE_NAME);
        const request = store.put(data, key);
        request.onsuccess = () => resolve();
        request.onerror = () => reject(request.error);
    });
};

const loadFromIndexedDB = async (key) => {
    const db = await openDB();
    return new Promise((resolve) => {
        const transaction = db.transaction([STORE_NAME], 'readonly');
        const store = transaction.objectStore(STORE_NAME);
        const request = store.get(key);
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => resolve(null);
    });
};

export function ToolsProvider({ children }) {
    const [allTools, setAllTools] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [loadProgress, setLoadProgress] = useState(0);
    const [isStreaming, setIsStreaming] = useState(false);

    useEffect(() => {
        loadToolsProgressively();
    }, []);

    const loadToolsProgressively = async () => {
        setLoading(true);
        setLoadProgress(0);
        setIsStreaming(true);

        try {
            // Check IndexedDB cache first
            const cachedTools = await loadFromIndexedDB('allTools');
            const cachedTimestamp = await loadFromIndexedDB('timestamp');
            const CACHE_DURATION = 24 * 60 * 60 * 1000; // 24 hours

            if (cachedTools && cachedTimestamp && (Date.now() - cachedTimestamp) < CACHE_DURATION) {
                setAllTools(cachedTools);
                setLoadProgress(50);
                setLoading(false);
                setIsStreaming(false);
                return;
            }

            const eventSource = new EventSource('https://koru-ai-eight.vercel.app/tools/stream' || 'https://koru-ai.onrender.com/tools/stream');
            
            let toolsArray = [];
            
            eventSource.onmessage = async (event) => {
                const data = JSON.parse(event.data);
                
                switch(data.type) {
                    case 'meta':
                        break;
                        
                    case 'chunk':
                        toolsArray = [...toolsArray, ...data.data];
                        setAllTools([...toolsArray]);
                        setLoadProgress(Math.round((data.progress / data.total) * 100));
                        break;
                        
                    case 'complete':
                        eventSource.close();
                        
                        // Save to IndexedDB
                        await saveToIndexedDB('allTools', toolsArray);
                        await saveToIndexedDB('timestamp', Date.now());
                        
                        setLoading(false);
                        setIsStreaming(false);
                        break;
                }
            };
            
            eventSource.onerror = (error) => {
                console.error('Streaming error:', error);
                eventSource.close();
                setError('Failed to load tools. Please refresh the page.');
                setLoading(false);
                setIsStreaming(false);
            };
            
        } catch (err) {
            console.error('Error:', err);
            setError(err.message);
            setLoading(false);
            setIsStreaming(false);
        }
    };

    const getToolsByCategory = (category) => {
        if (!allTools.length) return [];
        if (category === 'All') return allTools;
        return allTools.filter(tool => {
            const toolCategory = tool.Category || tool.category || 'Uncategorized';
            return toolCategory === category;
        });
    };

    const searchTools = (query) => {
        if (!query || !allTools.length) return allTools;
        const searchLower = query.toLowerCase();
        return allTools.filter(tool => {
            const name = tool.Name || tool.name || '';
            const desc = tool['Short Description'] || tool.description || '';
            return name.toLowerCase().includes(searchLower) || 
                   desc.toLowerCase().includes(searchLower);
        });
    };

    const getAllCategories = () => {
        if (!allTools.length) return ['All'];
        const categories = new Set();
        allTools.forEach(tool => {
            const cat = tool.Category || tool.category || 'Uncategorized';
            categories.add(cat);
        });
        return ['All', ...Array.from(categories).sort()];
    };

    const value = {
        allTools,
        loading,
        error,
        loadProgress,
        isStreaming,
        getToolsByCategory,
        searchTools,
        getAllCategories,
        refreshData: () => {
            saveToIndexedDB('allTools', null);
            saveToIndexedDB('timestamp', null);
            loadToolsProgressively();
        }
    };

    return (
        <ToolsContext.Provider value={value}>
            {children}
        </ToolsContext.Provider>
    );
}

export const useTools = () => useContext(ToolsContext);